/**
 * طرف‌حساب‌ها و شرکت‌های زنجیره تأمین (v2.125) — تأمین‌کنندهٔ نخ، بافندگی، رنگرزی، انبار و سایر.
 * شرکتِ تازه = یک ردیف؛ از همان لحظه در همهٔ فرم‌ها، ایمپورت، داشبورد و خروجی‌ها انتخاب‌پذیر است.
 * حذف نداریم (سوابق به طرف‌حساب وصل‌اند) — «غیرفعال» از فهرست‌های انتخاب بیرونش می‌برد.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Space, Tag, Select, Input, message, Switch, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { scGetParties, scUpdateParty } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import GxTable from "../../components/GxTable";
import { toJalali } from "../../utils/dateUtils";
import { faNum } from "../../utils/faNumberFormat";
import { PARTY_TYPE_LABEL } from "./scConstants";
import { PartyFormModal, PARTY_TYPE_OPTIONS } from "./PartyPicker";

const TYPE_COLOR = {
  supplier: "blue", knitting_workshop: "purple", dyeing_provider: "magenta",
  internal_warehouse: "green", other: "default",
};

export default function PartiesPage() {
  const { can } = useAuth();
  const canManage = can("sc.party.manage");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(null);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);   // null | {} (جدید) | party (ویرایش)

  const load = async () => {
    setLoading(true);
    try { setRows((await scGetParties()).data); } catch { message.error("خطا در بارگذاری"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const data = useMemo(() => rows.filter((p) => (!type || p.party_type === type)
    && (!q || String(p.name).includes(q) || String(p.notes || "").includes(q))), [rows, type, q]);

  const toggleActive = async (p, on) => {
    try { await scUpdateParty(p.id, { is_active: on }); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const columns = [
    { title: "نام", dataIndex: "name", width: 200, sorter: (a, b) => String(a.name).localeCompare(String(b.name), "fa"),
      gxFilter: "text", render: (v, r) => <b style={{ opacity: r.is_active ? 1 : 0.55 }}>{v}</b> },
    { title: "نقش", dataIndex: "party_type", width: 170, gxFilter: "enum",
      gxFilterOptions: PARTY_TYPE_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      sorter: (a, b) => String(a.party_type).localeCompare(String(b.party_type)),
      render: (v) => <Tag color={TYPE_COLOR[v]}>{PARTY_TYPE_LABEL[v] || v}</Tag>,
      exportValue: (v) => PARTY_TYPE_LABEL[v] || v },
    { title: "فاکتورها", dataIndex: "event_count", width: 90, align: "center",
      sorter: (a, b) => (a.event_count || 0) - (b.event_count || 0), render: (v) => faNum(v || 0) },
    { title: "توضیح", dataIndex: "notes", ellipsis: true, gxFilter: "text", render: (v) => v || "—" },
    { title: "ثبت", dataIndex: "created_at", width: 105,
      sorter: (a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")),
      render: (v) => (v ? toJalali(String(v).slice(0, 10)) : "—") },
    { title: "فعال", dataIndex: "is_active", width: 80, align: "center", gxFilter: "bool",
      render: (v, r) => (
        <Tooltip title={v ? "در فهرست‌های انتخاب هست" : "غیرفعال — سوابق می‌ماند"}>
          <Switch size="small" checked={v} disabled={!canManage} onChange={(on) => toggleActive(r, on)} />
        </Tooltip>
      ),
      exportValue: (v) => (v ? "فعال" : "غیرفعال") },
    { title: "", key: "actions", width: 60, gxExport: false, gxFilter: false,
      render: (_, r) => canManage && <Button size="small" icon={<EditOutlined />} onClick={() => setModal(r)} /> },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0 }}>طرف‌حساب‌ها و شرکت‌ها</h2>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
          {canManage && <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({})}>شرکتِ جدید</Button>}
        </Space>
      </div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select allowClear placeholder="همهٔ نقش‌ها" style={{ width: 220 }} value={type} onChange={setType}
            options={PARTY_TYPE_OPTIONS} />
          <Input.Search allowClear placeholder="جست‌وجوی نام/توضیح" style={{ width: 220 }}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </Space>
      </Card>
      <Card>
        <GxTable layoutId="sc-parties" gxExportName="طرف‌حساب‌های زنجیره تأمین" rowKey="id" size="small"
          loading={loading} dataSource={data} columns={columns} scroll={{ x: "max-content" }} />
      </Card>
      <PartyFormModal open={modal !== null} initial={modal || {}} defaultType={type || "supplier"}
        onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
    </div>
  );
}
